import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, PlayCircle, Eye, EyeOff } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import api from '../services/api';

// Brand colors
const BRAND_BLUE = '#2B4B6A';
const BRAND_ORANGE = '#F58A5A';

const AdminVideos = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [formData, setFormData] = useState({
    title: '', youtube_url: '', description: '', display_order: 0, is_active: true
  });

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await api.get('/admin/videos');
      setVideos(response.data);
    } catch (error) {
      toast.error('Failed to load videos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingVideo) {
        await api.put(`/admin/videos/${editingVideo.id}`, formData);
        toast.success('Video updated successfully');
      } else {
        await api.post('/admin/videos', formData);
        toast.success('Video added successfully');
      }
      setShowModal(false);
      resetForm();
      fetchVideos();
    } catch (error) {
      toast.error('Failed to save video');
    }
  };

  const handleEdit = (video) => {
    setEditingVideo(video);
    setFormData({
      title: video.title,
      youtube_url: video.youtube_url,
      description: video.description || '',
      display_order: video.display_order || 0,
      // Backend stores is_active as boolean. Truthy check works for either
      // storage form (bool True or legacy int 1).
      is_active: !!video.is_active
    });
    setShowModal(true);
  };

  const handleToggleActive = async (video) => {
    // is_active in the DB is a boolean; use a plain boolean flip (was
    // '!== 1' which always sent true for real boolean values -> nothing
    // ever toggled off).
    const next = !video.is_active;
    try {
      await api.put(`/admin/videos/${video.id}`, {
        is_active: next
      });
      toast.success(next ? 'Shown' : 'Hidden');
      fetchVideos();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;
    try {
      await api.delete(`/admin/videos/${id}`);
      toast.success('Video deleted');
      fetchVideos();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const resetForm = () => {
    setEditingVideo(null);
    setFormData({ title: '', youtube_url: '', description: '', display_order: 0, is_active: true });
  };

  // Extract YouTube video ID from URL
  const getYouTubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url?.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300" style={{ borderTopColor: BRAND_BLUE }}></div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-8 bg-gray-50/50 min-h-screen">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Educational Videos</h1>
            <p className="text-gray-500 text-sm mt-1">Manage YouTube video embeds</p>
          </div>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="text-white px-4 py-2.5 rounded-xl font-medium flex items-center gap-2 text-sm"
            style={{ backgroundColor: BRAND_BLUE }}
          >
            <Plus className="w-5 h-5" />
            Add Video
          </button>
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {editingVideo ? 'Edit Video' : 'Add New Video'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:outline-none text-sm"
                    placeholder="Video title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">YouTube URL *</label>
                  <input
                    type="url"
                    required
                    value={formData.youtube_url}
                    onChange={(e) => setFormData({...formData, youtube_url: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:outline-none text-sm"
                    placeholder="https://www.youtube.com/watch?v=..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows={3}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:outline-none text-sm"
                    placeholder="Brief description of the video"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Display Order</label>
                  <input
                    type="number"
                    value={formData.display_order}
                    onChange={(e) => setFormData({...formData, display_order: parseInt(e.target.value) || 0})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:outline-none text-sm"
                    placeholder="0"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    className="rounded border-gray-300"
                    style={{ accentColor: BRAND_BLUE }}
                  />
                  <label htmlFor="is_active" className="text-sm text-gray-700">Active (visible to users)</label>
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 text-white px-4 py-2.5 rounded-xl font-medium text-sm"
                    style={{ backgroundColor: BRAND_BLUE }}
                  >
                    {editingVideo ? 'Update' : 'Add'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Videos Grid */}
        {videos.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center shadow-sm">
            <PlayCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-600 mb-2">No videos yet</h3>
            <p className="text-gray-500">Add your first educational video.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.map((video) => {
              const videoId = getYouTubeId(video.youtube_url);
              return (
                <div key={video.id} className={`bg-white rounded-2xl border shadow-sm overflow-hidden ${!video.is_active ? 'opacity-60' : 'border-gray-100'}`}>
                  {videoId && (
                    <div className="relative pt-[56.25%] bg-gray-100">
                      <img
                        src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                        alt={video.title}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                        <PlayCircle className="w-12 h-12 text-white" />
                      </div>
                    </div>
                  )}
                  <div className="p-4">
                    <h3 className="font-medium text-gray-900 mb-1">{video.title}</h3>
                    {video.description && (
                      <p className="text-sm text-gray-500 mb-3 line-clamp-2">{video.description}</p>
                    )}
                    <div className="flex items-center justify-between">
                      <span className={`text-xs px-2.5 py-1 rounded-full ${video.is_active ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                        {video.is_active ? 'Active' : 'Hidden'}
                      </span>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleToggleActive(video)}
                          className="text-gray-400 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100"
                          title={video.is_active ? 'Hide' : 'Show'}
                        >
                          {video.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => handleEdit(video)}
                          className="text-gray-400 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(video.id)}
                          className="text-gray-400 hover:text-red-600 p-2 rounded-lg hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Sidebar>
  );
};

export default AdminVideos;
